import type { FC } from 'react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

interface TreeCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  readOnly: boolean;
}

/**
 * TreeCheckbox — Stateless appearance primitive.
 *
 * Renders the checkbox in exactly 3 visual states:
 * - Unchecked: empty bordered box
 * - Checked: filled box with check SVG
 * - Indeterminate: filled box with dash SVG
 *
 * This component is purely presentational:
 * NO state, NO hooks, NO event handlers, NO useTree import.
 */
export const TreeCheckbox: FC<TreeCheckboxProps> = ({
  checked,
  indeterminate,
  disabled,
  readOnly,
}) => {
  const isFilled = checked || indeterminate;

  const containerClasses = cn(
    // Base sizing and shape
    'w-4 h-4 rounded-[4px] flex items-center justify-center',
    // Visual state
    isFilled
      ? 'bg-primary-600 border-primary-600 shadow-button-inset dark:bg-primary-300'
      : 'border border-border-primary bg-bg-primary dark:border-dark-border-primary dark:bg-dark-bg-secondary',
    // Modifiers
    disabled && 'opacity-50',
    readOnly && !disabled && 'opacity-70',
  );

  return (
    <span data-testid="tree-checkbox" className={containerClasses}>
      {checked && !indeterminate && <CheckIcon />}
      {indeterminate && <DashIcon />}
    </span>
  );
};

TreeCheckbox.displayName = 'TreeCheckbox';

/** 12x12 check SVG icon — white stroke on transparent */
const CheckIcon: FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2 6L5 9L10 3"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 12x12 dash SVG icon — white stroke on transparent */
const DashIcon: FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 6H9.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
